import { Injectable, inject } from '@angular/core';
import { HeaderService } from '@shared/services';
import builderQuery, { QueryOptions } from 'odata-query';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  ExportCountryInformation,
  FileResponse,
  GstarClient,
  InvestmentJurisdictionWithNestedObjectsResponseAuditable,
} from '../../../web-api-client';
import { mapInvestmentJurisdictionFromResponse } from '../mappers';
import { mapInvestmentJurisdictionToCreateRequest } from '../mappers/investment-jurisdiction/map-investment-jurisdiction-to-create-request';
import { mapInvestmentJurisdictionToUpdateRequest } from '../mappers/investment-jurisdiction/map-investment-jurisdiction-to-update-request';
import { Collection, InvestmentJurisdiction } from '../models';
import { InvestmentJurisdictionQuery } from '@shared/typings/investment-juridisction-query.type';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';

@Injectable({
  providedIn: 'root',
})
export class InvestmentJurisdictionService {
  private readonly gstarClient = inject(GstarClient);
  private readonly headerService = inject(HeaderService);

  private cache$!: Observable<Collection<InvestmentJurisdiction>> | null;

  /** Separate cache for left nav dropdown (can use different API later if needed) */
  private navCache$!: Observable<Collection<InvestmentJurisdiction>> | null;

  private reloadCountryListSubj: BehaviorSubject<void> =
    new BehaviorSubject<void>(undefined);

  /** Sidebar nav dropdown should refetch after create/update */
  private readonly reloadNavForSidebarSubj = new Subject<void>();
  readonly reloadNavForSidebar$ = this.reloadNavForSidebarSubj.asObservable();

  countryList: Observable<Collection<InvestmentJurisdiction>> = combineLatest([
    this.reloadCountryListSubj,
  ]).pipe(
    switchMap(() => {
      return this.activeInvestmentJurisdictionList();
    })
  );

  reloadCountryList(): void {
    this.reloadCountryListSubj.next();
  }

  private invalidateInvestmentJurisdictionCachesAndNotifyNav(): void {
    this.cache$ = null;
    this.navCache$ = null;
    this.reloadNavForSidebarSubj.next();
  }

  getAllInvestmentJurisdiction(
    query?: InvestmentJurisdictionQuery
  ): Observable<Collection<InvestmentJurisdiction>> {
    return this.gstarClient
      .investmentjurisdictionsGET(
        undefined,
        buildQueryFilter(query),
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(response =>
              mapInvestmentJurisdictionFromResponse(response)
            ) ?? [],
        }))
      );
  }

  activeInvestmentJurisdictionList(): Observable<
    Collection<InvestmentJurisdiction>
  > {
    if (!this.cache$ || this.headerService.updatedClient()) {
      this.headerService.updatedClient.set(false);
      this.navCache$ = null; // invalidate nav cache so it refreshes too
      this.cache$ = this.gstarClient
        .byclientAll2(this.headerService.selectedClient()?.id)
        .pipe(
          map(
            (
              invJurisdictionResponse: InvestmentJurisdictionWithNestedObjectsResponseAuditable[]
            ) => ({
              total: invJurisdictionResponse.length ?? 0,
              items:
                invJurisdictionResponse?.map(invJurisdiction =>
                  mapInvestmentJurisdictionFromResponse(invJurisdiction)
                ) ?? [],
            })
          ),
          shareReplay(1)
        );
    }

    return this.cache$;
  }

  /**
   * Fetches investment jurisdictions for the left side navigation combobox.
   * Separate from activeInvestmentJurisdictionList so nav dropdown can use a different API or logic if needed.
   */
  getInvestmentJurisdictionsForNav(): Observable<
    Collection<InvestmentJurisdiction>
  > {
    if (!this.navCache$ || this.headerService.updatedClient()) {
      this.headerService.updatedClient.set(false);
      this.cache$ = null; // invalidate main cache so it refreshes too
      this.navCache$ = this.gstarClient
        .byclientnav(this.headerService.selectedClient()?.id)
        .pipe(
          map(
            (
              invJurisdictionResponse: InvestmentJurisdictionWithNestedObjectsResponseAuditable[]
            ) => ({
              total: invJurisdictionResponse.length ?? 0,
              items:
                invJurisdictionResponse?.map(invJurisdiction =>
                  mapInvestmentJurisdictionFromResponse(invJurisdiction)
                ) ?? [],
            })
          ),
          shareReplay(1)
        );
    }
    return this.navCache$;
  }

  get(
    query?: Partial<QueryOptions<InvestmentJurisdiction>>
  ): Observable<Collection<InvestmentJurisdiction>> {
    let orderBy: string | undefined = undefined;
    if (Array.isArray(query?.orderBy)) {
      orderBy = query!.orderBy.join(',');
    } else if (typeof query?.orderBy === 'string') {
      orderBy = query.orderBy;
    }

    let filterStr: string | undefined;
    if (query?.filter) {
      filterStr = builderQuery({ filter: query.filter }).replace(
        '?$filter=',
        ''
      );
    }

    return this.gstarClient
      .investmentjurisdictionsGET(
        undefined,
        filterStr,
        query?.top?.toString(),
        query?.skip?.toString(),
        orderBy,
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(response =>
              mapInvestmentJurisdictionFromResponse(response)
            ) ?? [],
        }))
      );
  }

  getById(
    investmentJurisdictionId: number
  ): Observable<InvestmentJurisdiction> {
    return this.gstarClient
      .investmentjurisdictionsGET2(investmentJurisdictionId)
      .pipe(map(response => mapInvestmentJurisdictionFromResponse(response)));
  }

  create(
    investmentJurisdiction: InvestmentJurisdiction
  ): Observable<InvestmentJurisdiction> {
    return this.gstarClient
      .investmentjurisdictionsPOST(
        mapInvestmentJurisdictionToCreateRequest(investmentJurisdiction)
      )
      .pipe(
        map(response => mapInvestmentJurisdictionFromResponse(response)),
        tap(() => this.invalidateInvestmentJurisdictionCachesAndNotifyNav())
      );
  }

  update(investmentJurisdiction: InvestmentJurisdiction): Observable<void> {
    return this.gstarClient
      .investmentjurisdictionsPUT(
        investmentJurisdiction.investmentJurisdictionId,
        mapInvestmentJurisdictionToUpdateRequest(investmentJurisdiction)
      )
      .pipe(
        tap(() => this.invalidateInvestmentJurisdictionCachesAndNotifyNav())
      );
  }

  exportCountryInformation(countryIds: number[]): Observable<FileResponse> {
    return this.gstarClient.exportcountryinformation(
      new ExportCountryInformation({ countryIds })
    );
  }
}
